import { IconButton } from "@controls";
import { BarsRegular, ChevronsLeftSolid, ChevronsRightSolid } from "@icons";
import * as Dialog from "@radix-ui/react-dialog";
import * as RadixNavigationMenu from "@radix-ui/react-navigation-menu";
import { DialogLiveRegions } from "@src/components/live-announcer";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";

import { useAppContext } from "../../contexts/app-context/hook";
import { useLocalstorage } from "../../hooks/helperHooks";

export interface GeneralLayoutProps {
  menuList: {
    key: string;
    component: React.FC<{
      minimized: boolean;
      setMobileMenuOpen: (open: boolean) => void;
    }>;
  }[];
  extraMenu: {
    key: string;
    component: React.FC<{
      minimized: boolean;
      setMobileMenuOpen: (open: boolean) => void;
    }>;
  }[];
  footer: React.ReactNode;
}

export function GeneralLayout({
  menuList,
  extraMenu = [],
  footer,
}: GeneralLayoutProps) {
  const { t } = useTranslation();
  const { mobileMenuOpen, setMobileMenuOpen } = useAppContext();
  const [storedMinimizedValue, setStoredMinimizedValue] = useLocalstorage(
    "MENU-MINIMIZED",
    "false",
  );
  const minimized = storedMinimizedValue === "true";

  return (
    <>
      <div
        className={classNames(
          "w-full h-full lg:p-4",
          "flex flex-row bg-surface",
        )}
      >
        <div
          className={classNames(
            "hidden md:flex flex-col justify-between m-2 lg:m-0",
            {
              "w-14": minimized,
              "min-w-[260px] max-w-[260px]": !minimized,
            },
          )}
        >
          <div className="flex flex-col justify-between">
            <div className="rounded flex-grow w-full">
              <RadixNavigationMenu.Root aria-label={t("mainNavigation")}>
                <RadixNavigationMenu.List
                  className={classNames("flex flex-col", {
                    "items-center": minimized,
                  })}
                >
                  {menuList.map((menuItems) => (
                    <RadixNavigationMenu.Item key={menuItems.key}>
                      <menuItems.component
                        minimized={minimized}
                        setMobileMenuOpen={setMobileMenuOpen}
                      />
                    </RadixNavigationMenu.Item>
                  ))}
                </RadixNavigationMenu.List>
              </RadixNavigationMenu.Root>
            </div>
          </div>
          <div>
            {extraMenu.length > 0 && (
              <RadixNavigationMenu.Root aria-label={t("secondaryNavigation")}>
                <RadixNavigationMenu.List
                  className={classNames("flex flex-col", {
                    "items-center": minimized,
                  })}
                >
                  {extraMenu.map((menuItems) => (
                    <RadixNavigationMenu.Item
                      key={menuItems.key}
                      className="w-full"
                    >
                      <menuItems.component
                        minimized={minimized}
                        setMobileMenuOpen={setMobileMenuOpen}
                      />
                    </RadixNavigationMenu.Item>
                  ))}
                </RadixNavigationMenu.List>
              </RadixNavigationMenu.Root>
            )}
            <div className="hidden md:flex w-full justify-end mt-2">
              <IconButton
                aria-label={minimized ? t("expandMenu") : t("collapseMenu")}
                size="medium"
                customIcon={
                  minimized ? <ChevronsRightSolid /> : <ChevronsLeftSolid />
                }
                onClick={() => setStoredMinimizedValue((!minimized).toString())}
              />
            </div>
          </div>
        </div>
        <main
          tabIndex={-1}
          className={classNames(
            "lg:ml-4 bg-page flex flex-grow flex-col h-full overflow-x-auto rounded-lg outline-none",
          )}
        >
          <div className="inline md:hidden">
            <Dialog.Root open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-scrim bg-opacity-50" />
                <Dialog.Content
                  aria-label={t("navigationMenu")}
                  aria-describedby={undefined}
                  className={classNames(
                    "fixed top-0 h-full w-2/3 max-w-[350px] overflow-auto p-2 flex flex-col justify-between",
                    "bg-primary text-on-primary",
                    {
                      "-left-52": !mobileMenuOpen,
                      "left-0": mobileMenuOpen,
                    },
                  )}
                >
                  <div>
                    <RadixNavigationMenu.Root aria-label={t("mainNavigation")}>
                      <RadixNavigationMenu.List className={"flex flex-col"}>
                        {menuList.map((menuItems) => (
                          <RadixNavigationMenu.Item key={menuItems.key}>
                            <menuItems.component
                              minimized={false}
                              setMobileMenuOpen={setMobileMenuOpen}
                            />
                          </RadixNavigationMenu.Item>
                        ))}
                      </RadixNavigationMenu.List>
                    </RadixNavigationMenu.Root>
                  </div>
                  <div>
                    {extraMenu.length > 0 && (
                      <RadixNavigationMenu.Root
                        aria-label={t("secondaryNavigation")}
                      >
                        <RadixNavigationMenu.List
                          className={classNames("flex flex-col", {
                            "items-center": minimized,
                          })}
                        >
                          {extraMenu.map((menuItems) => (
                            <RadixNavigationMenu.Item
                              key={menuItems.key}
                              className="w-full"
                            >
                              <menuItems.component
                                minimized={minimized}
                                setMobileMenuOpen={setMobileMenuOpen}
                              />
                            </RadixNavigationMenu.Item>
                          ))}
                        </RadixNavigationMenu.List>
                      </RadixNavigationMenu.Root>
                    )}
                  </div>
                  <DialogLiveRegions open={mobileMenuOpen} />
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          </div>
          <div
            className={classNames(
              "flex md:hidden shrink-0",
              "justify-between items-center w-full min-h-11 py-[7px] px-2 border-b bg-tint",
            )}
          >
            <button
              aria-label={t("openMenu")}
              className="w-target h-target rounded text-primary"
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
              }}
            >
              <BarsRegular />
            </button>
          </div>
          <div className="m-2 flex-auto overflow-y-auto">
            <Outlet />
          </div>
          <footer className="shrink-0 flex justify-end border-t px-2 py-[4px]">
            {footer}
          </footer>
        </main>
      </div>
    </>
  );
}

export default GeneralLayout;
